import { createFileRoute } from "@tanstack/react-router";

import { retainedQueryData } from "@sx/api-client";
import { deliveryReviewOnly, useLibraryAccess } from "../../../workspaces/library/access";
import { trimmedStringParam } from "../../../lib/url-state";
import { ProjectAccessRole } from "../../../lib/account";
import { useProject } from "../../../shell/project";
import { LibraryPage, type LibrarySearch } from "../../../workspaces/library/pages/LibraryPage";
import { parseEpisodesSearch } from "../../../workspaces/library/pages/episodes-search";
import { isShelf } from "../../../workspaces/library/kinds";
import { lazy, Suspense, useCallback } from "react";
import { RouteFallback } from "../../../shell/RouteFallback";
import { prefetchForProject } from "../../../lib/prefetch";

// Only the selected reader loads its rendering machinery. Published prose includes
// diagrams; delivery decisions are separate from browsing an episode list.
const PublishedResultsPage = lazy(() =>
  import("../../../workspaces/agents/PublishedResultsPage").then(module => ({ default: module.PublishedResultsPage })),
);
const DeliveryReviewsPage = lazy(() =>
  import("../../../workspaces/library/pages/DeliveryReviewsPage").then(module => ({ default: module.DeliveryReviewsPage })),
);

export interface LibraryBrowseSearch extends LibrarySearch {
  readonly result?: string;
  readonly published?: boolean;
  readonly delivery?: string;
  readonly reviews?: boolean;
}

export function parseLibraryBrowseSearch(
  search: Record<string, unknown>,
): LibraryBrowseSearch {
  const result = trimmedStringParam(search.result);
  const delivery = trimmedStringParam(search.delivery);
  return {
    result:
      result &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        result,
      )
        ? result
        : undefined,
    published: search.published === true || undefined,
    delivery: delivery && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(delivery) ? delivery : undefined,
    reviews: search.reviews === true || undefined,
    ...parseEpisodesSearch(search),
    tab: isShelf(search.tab) ? search.tab : search.tab === "agents" ? "agents" : undefined,
  };
}

export const Route = createFileRoute("/_console/p/$org/$project/library/")({
  validateSearch: parseLibraryBrowseSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, params, deps }) =>
    prefetchForProject(
      queryClient,
      params,
      Promise.all([
        import("../../../workspaces/library/api"),
        import("../../../workspaces/library/queries"),
        import("../../../workspaces/library/episode-query"),
      ]),
      async ([{ createCatalogApi }, { libraryQueries }, { episodeListRead }], projectId) => {
        if (deps.result || deps.published || deps.delivery || deps.reviews || deps.tab === "agents") return;
        const api = createCatalogApi(projectId);
        if (deps.tab !== undefined && deps.tab !== "episodes") {
          return queryClient.prefetchQuery(libraryQueries(projectId, api).overview);
        }
        const page = episodeListRead(projectId, api, deps, { timeZone: "UTC", now: Date.now() });
        // Date searches use the component's hydrated clock; plain browsing has no zone.
        if (page.day.day !== undefined) return;
        return queryClient.prefetchInfiniteQuery(page.options);
      },
    ),
  component: LibraryOverviewRoute,
});

function LibraryOverviewRoute() {
  const search = Route.useSearch();
  const { result, published, delivery, reviews } = search;
  const navigate = Route.useNavigate();
  const onStateChange = useCallback(
    (patch: LibrarySearch) =>
      void navigate({ search: (current) => ({ ...current, ...patch }), replace: !("tab" in patch) }),
    [navigate],
  );
  const reviewOnly =
    useProject().selected?.managedAccessRole ===
    ProjectAccessRole.RESULT_REVIEWER;
  const access = useLibraryAccess(!reviewOnly && !result && !published);
  const decisionOnly = deliveryReviewOnly(retainedQueryData(access)?.scopes ?? []);
  return (
    <Suspense fallback={<RouteFallback />}>
      {!reviewOnly && (decisionOnly || delivery || reviews) ? (
        <DeliveryReviewsPage deliveryId={delivery} />
      ) : reviewOnly || result || published ? (
        <PublishedResultsPage resultId={result} />
      ) : (
        <LibraryPage state={search} onStateChange={onStateChange} />
      )}
    </Suspense>
  );
}
